import { QUERY_PROGRESS_PENDING, TRACK_EVENTS } from "core/consts";
import { FileTableActions, Filev2, QueryProgress } from "core/types";
import GeneralTable from "ds_legacy/components/Tables/GeneralTable";
import {
  COLUMN_TYPE_DEFAULT_DATE,
  COLUMN_TYPE_DEFAULT_TOOLTIP,
  COLUMN_TYPE_FILES_CATEGORY,
  COLUMN_TYPE_FILES_FILE_NAME,
  COLUMN_TYPE_FILES_SHARED_WITH,
} from "ds_legacy/components/Tables/GeneralTable/CustomCells";
import { EmptyV2 } from "ds_legacy/components/Tables/GeneralTable/EmptyComponent";
import { SetAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import {
  getAllowList,
  getFileListActionOptions,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions/Files";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { getFileNameAndIsNewVersionAvailable } from "dsl/ecosystems/PatientFiles/utils";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import { EncryptedFileMessage, FilesTableTitle } from ".";

/** Column width percentages for ReceivedFiles — must sum to 100%. Messenger shows 3 columns; default shows 5. Min widths avoid truncation (e.g. category needs room for label + icon). */
const RECEIVED_FILES_WIDTHS = {
  default: {
    category: "10%",
    fileName: "28%",
    shareDate: "24%",
    sharedWith: "20%",
    uploadedBy: "18%",
  },
  messenger: { category: "18%", fileName: "40%", shareDate: "42%" },
} as const;

export type ReceivedFilesProps = {
  canAccessPatientData: boolean;
  canReceiveFiles?: boolean;
  careseekerId: number | null;
  files?: Filev2[] | null;
  isMessenger?: boolean;
  queryProgress: QueryProgress;
  setAction: SetAction<FileFeatureData>;
};

export function ReceivedFiles({
  canAccessPatientData,
  canReceiveFiles = true,
  careseekerId,
  files,
  isMessenger,
  queryProgress,
  setAction,
}: ReceivedFilesProps) {
  const translations = useTranslations();
  const { trackEvent } = useTracking();

  const description = isMessenger ? (
    <EncryptedFileMessage />
  ) : (
    translations.fileSection.fileUploadComponent.descriptionFilesReceived
  );

  return (
    <VerticalLayout>
      <FilesTableTitle
        description={description}
        isMessenger={isMessenger}
        numberOfFiles={files?.length ?? 0}
        title={translations.fileSection.fileListComponent.titleFilesReceived}
      />
      <GeneralTable<Filev2, FileTableActions>
        actions={{
          allowList: () => getAllowList({ fileTable: "received" }),
          getOptions: ({ data: file, translations }) =>
            getFileListActionOptions({
              canAccessPatientData,
              file,
              fileTable: "received",
              setAction,
              trackEvent,
              translations,
            }),
          getTestId: (file) => `action-received-file-${file.id}`,
          onActionMenuClick: () => {
            trackEvent({
              file_implementation: "HIS",
              name: TRACK_EVENTS.FILE_MENU_CLICKED,
              section: "received",
            });
          },
        }}
        columns={[
          {
            getProps: "category",
            title: translations.fileSection.fileListComponent.category,
            type: COLUMN_TYPE_FILES_CATEGORY,
            width: isMessenger
              ? RECEIVED_FILES_WIDTHS.messenger.category
              : RECEIVED_FILES_WIDTHS.default.category,
          },
          {
            getProps: (file) =>
              getFileNameAndIsNewVersionAvailable({
                canAccessPatientData,
                file,
              }),
            title: translations.fileSection.fileListComponent.fileName,
            type: COLUMN_TYPE_FILES_FILE_NAME,
            width: isMessenger
              ? RECEIVED_FILES_WIDTHS.messenger.fileName
              : RECEIVED_FILES_WIDTHS.default.fileName,
          },
          {
            getProps: ({ careprovider, careseeker }) => ({
              value: careprovider?.name ?? careseeker?.name,
            }),
            isHidden: isMessenger,
            title: translations.fileSection.fileListComponent.uploadedBy,
            type: COLUMN_TYPE_DEFAULT_TOOLTIP,
            width: isMessenger
              ? undefined
              : RECEIVED_FILES_WIDTHS.default.uploadedBy,
          },
          {
            getProps: ({ created_at }) => ({
              value: created_at,
            }),
            title: translations.fileSection.fileListComponent.shareDate,
            type: COLUMN_TYPE_DEFAULT_DATE,
            width: isMessenger
              ? RECEIVED_FILES_WIDTHS.messenger.shareDate
              : RECEIVED_FILES_WIDTHS.default.shareDate,
          },
          {
            getProps: ({ share_mode, shared_with }) => ({
              careseekerId,
              share_mode,
              shared_with,
            }),
            isHidden: isMessenger,
            title: translations.fileSection.fileListComponent.sharedWith,
            type: COLUMN_TYPE_FILES_SHARED_WITH,
            width: isMessenger
              ? undefined
              : RECEIVED_FILES_WIDTHS.default.sharedWith,
          },
        ]}
        data={files || []}
        emptyComponent={
          <EmptyV2
            isMessenger={isMessenger}
            title={
              canReceiveFiles
                ? translations.fileSection.fileListComponent
                    .placeholderNothingReceived
                : translations.messenger.sender.fileRestrictionHint
            }
          />
        }
        id="received-files"
        isLoading={queryProgress === QUERY_PROGRESS_PENDING}
      />
    </VerticalLayout>
  );
}
export default ReceivedFiles;
